
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { AuthError, AuthApiError } from "@supabase/supabase-js";

export const useAuthFlow = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const getErrorMessage = (error: AuthError | AuthApiError | Error) => {
    console.error("Auth error details:", error);
    
    if ('status' in error) {
      switch (error.status) {
        case 400:
          if (error.message.includes("Email already registered")) {
            return "Este email já está registrado";
          }
          return "Email ou senha inválidos";
        case 422:
          return "Formato de email ou senha inválido.";
        case 500:
          return "Erro interno do servidor. Por favor, tente novamente.";
        default:
          return "Erro ao fazer login. Por favor, tente novamente.";
      }
    }
    return error.message || "Erro ao fazer login. Por favor, tente novamente.";
  };

  const signIn = async (email: string, password: string) => {
    try {
      setIsLoading(true);
      console.log("LoginFlow: Iniciando processo de login...");

      // Tentar autenticação com o sistema de auth do Supabase
      const { data: authData, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      // Se falhar no auth, tentar com a tabela users customizada
      if (signInError) {
        console.log("LoginFlow: Falha no auth, verificando usuário manual:", signInError);
        
        // Verificar se o usuário existe na tabela users
        const { data: manualUser, error: userCheckError } = await supabase
          .from('users')
          .select('*')
          .eq('email', email)
          .eq('password', password)  // Nota: Isso não é seguro para produção (senhas em texto simples)
          .maybeSingle();
          
        if (userCheckError || !manualUser) {
          console.error("LoginFlow: Usuário não encontrado ou credenciais inválidas");
          throw new Error("Email ou senha inválidos");
        }
        
        console.log("LoginFlow: Usuário encontrado no sistema manual:", manualUser);
        
        // Se o usuário não tiver auth_id, criar um no sistema de auth
        if (!manualUser.auth_id) {
          console.log("LoginFlow: Criando usuário no sistema de auth...");
          const { data: newAuthUser, error: createAuthError } = await supabase.auth.signUp({
            email,
            password,
            options: {
              data: {
                name: manualUser.name,
                type: manualUser.type,
              }
            }
          });
          
          if (createAuthError) {
            console.error("LoginFlow: Erro ao criar usuário no auth:", createAuthError);
            throw createAuthError;
          }
          
          if (newAuthUser.user) {
            // Atualizar o usuário com o novo auth_id
            const { error: updateError } = await supabase
              .from('users')
              .update({ auth_id: newAuthUser.user.id })
              .eq('id', manualUser.id);
              
            if (updateError) {
              console.error("LoginFlow: Erro ao atualizar auth_id:", updateError);
              // Continuar mesmo com erro
            }
            
            // Atualizamos o usuário com o novo auth_id
            manualUser.auth_id = newAuthUser.user.id;
          }
        } else {
          // Se já tem auth_id, tentar fazer login direto
          const { error: authLoginError } = await supabase.auth.signInWithPassword({
            email,
            password,
          });
          
          if (authLoginError) {
            console.log("LoginFlow: Erro no login com auth_id existente:", authLoginError);
            // Se falhar, significa que o auth_id existe mas a senha pode estar diferente
            // Neste caso, atualizamos a senha no sistema auth para corresponder à senha manual
            try {
              // Requer admin role para fazer isso
              const { error: resetError } = await supabase.auth.admin.updateUserById(
                manualUser.auth_id,
                { password }
              );
              
              if (resetError) {
                console.error("LoginFlow: Erro ao atualizar senha no auth:", resetError);
              } else {
                // Tentar login novamente após atualizar senha
                await supabase.auth.signInWithPassword({
                  email,
                  password,
                });
              }
            } catch (adminError) {
              console.error("LoginFlow: Falha ao sincronizar senha:", adminError);
              // Continuar mesmo com erro
            }
          }
        }
        
        return manualUser;
      }

      if (!authData.session) {
        console.error("LoginFlow: Sessão não iniciada");
        throw new Error("Não foi possível iniciar a sessão");
      }

      console.log("LoginFlow: Verificando se usuário existe na tabela users...");
      const { data: existingUser, error: userCheckError } = await supabase
        .from('users')
        .select('*')
        .eq('email', email)
        .maybeSingle();

      if (userCheckError) {
        console.error("LoginFlow: Erro ao verificar usuário:", userCheckError);
        await supabase.auth.signOut();
        throw new Error("Erro ao verificar usuário no sistema");
      }

      // Se não encontrar o usuário na tabela customizada, criar um
      if (!existingUser) {
        console.log("LoginFlow: Usuário não encontrado na tabela users, criando...");
        
        // Buscar dados do usuário autenticado
        const { data: userData } = await supabase.auth.getUser();
        
        if (!userData || !userData.user) {
          await supabase.auth.signOut();
          throw new Error("Erro ao obter dados do usuário");
        }
        
        // Criar um novo usuário com os dados do auth
        const { data: newUser, error: createError } = await supabase
          .from('users')
          .insert([{
            name: userData.user.user_metadata.name || email.split('@')[0],
            email: email,
            type: 'user',  // Tipo padrão
            auth_id: userData.user.id,
            created_at: new Date(),
            updated_at: new Date(),
            status: 'active'
          }])
          .select()
          .single();
          
        if (createError) {
          console.error("LoginFlow: Erro ao criar usuário:", createError);
          await supabase.auth.signOut();
          throw new Error("Erro ao criar usuário no sistema");
        }
        
        console.log("LoginFlow: Usuário criado com sucesso:", newUser);
        return newUser;
      }
      
      // Se o usuário existe mas não tem auth_id, atualizar
      if (!existingUser.auth_id && authData.user) {
        console.log("LoginFlow: Atualizando auth_id do usuário:", existingUser.id);
        const { error: updateError } = await supabase
          .from('users')
          .update({ auth_id: authData.user.id })
          .eq('id', existingUser.id);
          
        if (updateError) {
          console.error("LoginFlow: Erro ao atualizar auth_id:", updateError);
          // Continuar mesmo com erro
        }
      }

      // Armazenar company ID para uso futuro
      if (existingUser.company_id) {
        localStorage.setItem('currentCompanyId', existingUser.company_id);
        if (authData.user?.id) {
          localStorage.setItem(`companyId_${authData.user.id}`, existingUser.company_id);
        }
        console.log("LoginFlow: Company ID saved:", existingUser.company_id);
      }

      console.log("LoginFlow: Login realizado com sucesso:", existingUser);
      return existingUser;

    } catch (error: any) {
      console.error("LoginFlow: Erro completo:", error);
      const errorMessage = error.message || getErrorMessage(error);
      toast({
        variant: "destructive",
        title: "Erro no login",
        description: errorMessage,
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const signUp = async (email: string, password: string, name: string) => {
    try {
      setIsLoading(true);
      console.log("SignupFlow: Iniciando processo de registro...");

      const { data: existingUser } = await supabase
        .from('users')
        .select('id')
        .eq('email', email)
        .maybeSingle();

      if (existingUser) {
        throw new Error("Este email já está cadastrado");
      }

      const { data: authData, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            name,
            type: 'user',
          },
        },
      });

      if (signUpError) {
        console.error("SignupFlow: Erro no registro:", signUpError);
        throw signUpError;
      }

      if (!authData.user) {
        throw new Error("Erro ao criar usuário");
      }

      // Aguardar um momento para garantir que o trigger tenha tempo de executar
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Verificar se o trigger criou o usuário
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('*')
        .eq('email', email)
        .maybeSingle();

      // Se o trigger não criou o usuário, criar manualmente
      if (userError || !userData) {
        console.log("SignupFlow: Criando usuário manualmente...");
        
        const { data: newUser, error: createError } = await supabase
          .from('users')
          .insert([{
            name: name,
            email: email,
            type: 'user',
            auth_id: authData.user.id,
            created_at: new Date(),
            updated_at: new Date(),
            status: 'active'
          }])
          .select()
          .single();
          
        if (createError) {
          console.error("SignupFlow: Erro ao criar usuário manualmente:", createError);
          throw new Error("Erro ao finalizar o cadastro do usuário");
        }
        
        console.log("SignupFlow: Usuário criado manualmente:", newUser);
        return newUser;
      }

      console.log("SignupFlow: Registro realizado com sucesso:", userData);
      return userData;

    } catch (error: any) {
      const errorMessage = getErrorMessage(error);
      toast({
        variant: "destructive",
        title: "Erro no registro",
        description: errorMessage,
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    signIn,
    signUp,
    isLoading,
  };
};
